<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { frontendURL } from 'dashboard/helper/URLHelper.js';
import ApiClient from 'dashboard/api/ApiClient';
import LabelsAPI from 'dashboard/api/labels';
import InboxesAPI from 'dashboard/api/inboxes';
import APIHelper from 'dashboard/helper/APIHelper';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import { useAlert } from 'dashboard/composables';

const MAX_IMAGE_BYTES = 10 * 1024 * 1024; // 10 MB
// Removed SettingsLayout imports - using standard dashboard layout
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { Multiselect } from 'vue-multiselect';

const { t } = useI18n();
const router = useRouter();

const TELEGRAM_ERROR_PATTERNS = [
  { pattern: /blocked by the user/i, key: 'BULK_MESSAGES.STATS_MODAL.ERROR_REASONS.BLOCKED' },
  { pattern: /user is deactivated/i, key: 'BULK_MESSAGES.STATS_MODAL.ERROR_REASONS.DEACTIVATED' },
  { pattern: /chat not found/i, key: 'BULK_MESSAGES.STATS_MODAL.ERROR_REASONS.CHAT_NOT_FOUND' },
  { pattern: /bot was kicked/i, key: 'BULK_MESSAGES.STATS_MODAL.ERROR_REASONS.KICKED' },
  { pattern: /too many requests|429/i, key: 'BULK_MESSAGES.STATS_MODAL.ERROR_REASONS.RATE_LIMIT' },
];

const friendlyError = raw => {
  if (!raw) return t('BULK_MESSAGES.STATS_MODAL.ERROR_REASONS.UNKNOWN');
  const found = TELEGRAM_ERROR_PATTERNS.find(({ pattern }) => pattern.test(raw));
  return found ? t(found.key) : t('BULK_MESSAGES.STATS_MODAL.ERROR_REASONS.OTHER');
};

const openConversation = (conversationDisplayId) => {
  if (!conversationDisplayId || !currentAccountId.value) return;
  const url = frontendURL(
    `accounts/${currentAccountId.value}/conversations/${conversationDisplayId}`
  );
  router.push(url);
};

// Data
const isLoading = ref(false);
const bulkMessages = ref([]);
const showCreateForm = ref(false);
const previewCount = ref(null);
const form = ref({
  name: '',
  message: '',
  image_url: '',
  scheduled_at: '',
  selectedLabels: [],
  selectedInboxes: [],
  selectedChannels: [],
  bonusCodeBatchId: '',
  bonusCodeMode: 'pool',
});
const availableLabels = ref([]);
const availableInboxes = ref([]);
const availableChannels = ref([]);
const availableBatches = ref([]);
const pollingInterval = ref(null);

// Image upload state
const imageFileInput = ref(null);
const isUploadingImage = ref(false);

const onImageFileChange = async (event) => {
  const file = event.target.files && event.target.files[0];
  if (!file) return;

  if (!file.type.startsWith('image/')) {
    useAlert(t('BULK_MESSAGES.FORM.IMAGE_UPLOAD_TYPE_ERROR'));
    event.target.value = '';
    return;
  }
  if (file.size > MAX_IMAGE_BYTES) {
    useAlert(t('BULK_MESSAGES.FORM.IMAGE_UPLOAD_SIZE_ERROR'));
    event.target.value = '';
    return;
  }

  try {
    isUploadingImage.value = true;
    const { fileUrl } = await uploadFile(file, currentAccountId.value);
    form.value.image_url = fileUrl;
  } catch (error) {
    useAlert(t('BULK_MESSAGES.FORM.IMAGE_UPLOAD_ERROR'));
  } finally {
    isUploadingImage.value = false;
    event.target.value = '';
  }
};

const clearImage = () => {
  form.value.image_url = '';
};

// Detail modal state
const showDetailModal = ref(false);
const detailLoading = ref(false);
const detailConversations = ref([]);
const detailTotal = ref(0);
const detailPage = ref(1);
const detailPerPage = ref(50);

// Stats modal state
const showStatsModal = ref(false);
const statsLoading = ref(false);
const statsData = ref(null);
const statsBulkMessageId = ref(null);
const failedLoading = ref(false);
const failedMessages = ref([]);
const failedTotal = ref(0);
const failedPage = ref(1);
const failedPerPage = ref(50);
const failedPageInput = ref(1);
const failedErrorKind = ref('');
const failedErrorKindCounts = ref({});

const PER_PAGE_OPTIONS = [25, 50, 100, 200];
const ERROR_KIND_OPTIONS = [
  { value: '', labelKey: 'BULK_MESSAGES.STATS_MODAL.FILTER_ALL' },
  { value: 'blocked', labelKey: 'BULK_MESSAGES.STATS_MODAL.ERROR_REASONS.BLOCKED' },
  { value: 'deactivated', labelKey: 'BULK_MESSAGES.STATS_MODAL.ERROR_REASONS.DEACTIVATED' },
  { value: 'chat_not_found', labelKey: 'BULK_MESSAGES.STATS_MODAL.ERROR_REASONS.CHAT_NOT_FOUND' },
  { value: 'kicked', labelKey: 'BULK_MESSAGES.STATS_MODAL.ERROR_REASONS.KICKED' },
  { value: 'rate_limit', labelKey: 'BULK_MESSAGES.STATS_MODAL.ERROR_REASONS.RATE_LIMIT' },
  { value: 'other', labelKey: 'BULK_MESSAGES.STATS_MODAL.ERROR_REASONS.OTHER' },
];

// Dialog refs
const deleteDialogRef = ref(null);
const scheduleDialogRef = ref(null);
const selectedBulkMessageId = ref(null);
const scheduledTime = ref('');

// API
const wootAPI = new ApiClient('bulk_messages', { accountScoped: true });
const authenticatedAxios = APIHelper(window.axios);
const currentAccountId = useMapGetter('getCurrentAccountId');
const apiBase = computed(
  () => `/api/v1/accounts/${currentAccountId.value}/bulk_messages`
);

// Computed
const labelOptions = computed(() => availableLabels.value || []);
const inboxOptions = computed(() => availableInboxes.value || []);
const channelOptions = computed(() => availableChannels.value || []);

const statusMap = computed(() => ({
  pending: t('BULK_MESSAGES.STATUS.PENDING'),
  processing: t('BULK_MESSAGES.STATUS.PROCESSING'),
  completed: t('BULK_MESSAGES.STATUS.COMPLETED'),
  failed: t('BULK_MESSAGES.STATUS.FAILED'),
  cancelled: t('BULK_MESSAGES.STATUS.CANCELLED'),
  scheduled: t('BULK_MESSAGES.STATUS.SCHEDULED'),
  paused: t('BULK_MESSAGES.STATUS.PAUSED'),
  resumed: t('BULK_MESSAGES.STATUS.RESUMED'),
}));

// Methods
const loadBulkMessages = async () => {
  try {
    isLoading.value = true;
    const response = await wootAPI.get();
    if (Array.isArray(response.data)) {
      bulkMessages.value = response.data;
    } else if (Array.isArray(response.data.payload)) {
      bulkMessages.value = response.data.payload;
    } else {
      bulkMessages.value = [];
    }
  } catch (error) {
    useAlert(t('BULK_MESSAGES.TOAST_MESSAGES.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const loadChannels = () => {
  availableChannels.value = availableInboxes.value
    .filter(inbox => inbox.channel_type === 'Channel::Telegram')
    .map(inbox => ({
      label: `${inbox.name} (${inbox.channel?.bot_name || 'Telegram'})`,
      value: inbox.id,
      inbox_id: inbox.id,
      channel_type: 'telegram',
    }));
};

const closeCreateForm = () => {
  showCreateForm.value = false;
  form.value = {
    name: '',
    message: '',
    image_url: '',
    scheduled_at: '',
    selectedLabels: [],
    selectedInboxes: [],
    selectedChannels: [],
    bonusCodeBatchId: '',
    bonusCodeMode: 'pool',
  };
  previewCount.value = null;
};

const loadBonusBatches = async () => {
  try {
    const response = await authenticatedAxios.get(
      `/api/v1/accounts/${currentAccountId.value}/bonus_code_batches`
    );
    availableBatches.value = response.data.payload || [];
  } catch (error) {
    availableBatches.value = [];
  }
};

const loadFilters = async () => {
  try {
    // Load labels
    const labelsResponse = await LabelsAPI.get();
    if (Array.isArray(labelsResponse.data.payload)) {
      availableLabels.value = labelsResponse.data.payload;
    } else if (Array.isArray(labelsResponse.data)) {
      availableLabels.value = labelsResponse.data;
    } else {
      availableLabels.value = [];
    }

    // Load inboxes
    const inboxesResponse = await InboxesAPI.get();
    if (Array.isArray(inboxesResponse.data.payload)) {
      availableInboxes.value = inboxesResponse.data.payload;
    } else if (Array.isArray(inboxesResponse.data)) {
      availableInboxes.value = inboxesResponse.data;
    } else {
      availableInboxes.value = [];
    }

    // Load channels (Telegram inboxes)
    loadChannels();
    // Load bonus code batches
    await loadBonusBatches();
  } catch (error) {
    availableLabels.value = [];
    availableInboxes.value = [];
    availableChannels.value = [];
  }
};

const buildFilterPayload = () => ({
  label_filter: JSON.stringify(form.value.selectedLabels.map(l => l.id)),
  inbox_filter: JSON.stringify(form.value.selectedInboxes.map(i => i.id)),
  channel_filter: JSON.stringify(
    form.value.selectedChannels.map(c => c.inbox_id)
  ),
});

const previewBulkMessage = async () => {
  try {
    const response = await authenticatedAxios.post(
      `${apiBase.value}/preview`,
      buildFilterPayload()
    );
    previewCount.value = response.data.target_count;
  } catch (error) {
    // Error previewing bulk message
  }
};

const loadTargetConversations = async (page = 1) => {
  try {
    detailLoading.value = true;
    const response = await authenticatedAxios.post(
      `${apiBase.value}/target_conversations`,
      {
        ...buildFilterPayload(),
        page,
        per_page: detailPerPage.value,
      }
    );
    detailConversations.value = response.data.conversations || [];
    detailTotal.value = response.data.total_count || 0;
    detailPage.value = response.data.page || 1;
  } catch (error) {
    detailConversations.value = [];
    detailTotal.value = 0;
  } finally {
    detailLoading.value = false;
  }
};

const openDetailModal = async () => {
  detailPage.value = 1;
  showDetailModal.value = true;
  await loadTargetConversations(1);
};

const closeDetailModal = () => {
  showDetailModal.value = false;
  detailConversations.value = [];
  detailTotal.value = 0;
  detailPage.value = 1;
};

const detailTotalPages = computed(() => {
  if (!detailTotal.value) return 1;
  return Math.ceil(detailTotal.value / detailPerPage.value);
});

const goToDetailPage = page => {
  if (page < 1 || page > detailTotalPages.value) return;
  loadTargetConversations(page);
};

const loadStats = async bulkMessageId => {
  try {
    statsLoading.value = true;
    const response = await authenticatedAxios.get(
      `${apiBase.value}/${bulkMessageId}/stats`
    );
    statsData.value = response.data;
  } catch (error) {
    statsData.value = null;
  } finally {
    statsLoading.value = false;
  }
};

const loadFailedMessages = async (bulkMessageId, page = 1) => {
  try {
    failedLoading.value = true;
    const params = {
      page,
      per_page: failedPerPage.value,
    };
    if (failedErrorKind.value) params.error_kind = failedErrorKind.value;
    const response = await authenticatedAxios.get(
      `${apiBase.value}/${bulkMessageId}/failed_messages`,
      { params }
    );
    failedMessages.value = response.data.messages || [];
    failedTotal.value = response.data.total_count || 0;
    failedPage.value = response.data.page || 1;
    failedPageInput.value = failedPage.value;
    failedErrorKindCounts.value = response.data.error_kind_counts || {};
  } catch (error) {
    failedMessages.value = [];
    failedTotal.value = 0;
  } finally {
    failedLoading.value = false;
  }
};

const applyFailedFilters = () => {
  if (!statsBulkMessageId.value) return;
  failedPage.value = 1;
  failedPageInput.value = 1;
  loadFailedMessages(statsBulkMessageId.value, 1);
};

const submitPageJump = () => {
  if (!statsBulkMessageId.value) return;
  let p = parseInt(failedPageInput.value, 10);
  if (Number.isNaN(p) || p < 1) p = 1;
  if (p > failedTotalPages.value) p = failedTotalPages.value;
  failedPageInput.value = p;
  loadFailedMessages(statsBulkMessageId.value, p);
};

const openStatsModal = async bulkMessageId => {
  statsBulkMessageId.value = bulkMessageId;
  failedPage.value = 1;
  failedPageInput.value = 1;
  failedErrorKind.value = '';
  showStatsModal.value = true;
  await Promise.all([
    loadStats(bulkMessageId),
    loadFailedMessages(bulkMessageId, 1),
  ]);
};

const closeStatsModal = () => {
  showStatsModal.value = false;
  statsData.value = null;
  failedMessages.value = [];
  failedTotal.value = 0;
  failedPage.value = 1;
  failedPageInput.value = 1;
  failedErrorKind.value = '';
  failedErrorKindCounts.value = {};
  statsBulkMessageId.value = null;
};

const failedTotalPages = computed(() => {
  if (!failedTotal.value) return 1;
  return Math.ceil(failedTotal.value / failedPerPage.value);
});

const goToFailedPage = page => {
  if (page < 1 || page > failedTotalPages.value || !statsBulkMessageId.value) return;
  loadFailedMessages(statsBulkMessageId.value, page);
};

const createBulkMessage = async () => {
  try {
    isLoading.value = true;
    await wootAPI.create({
      bulk_message: {
        name: form.value.name,
        message: form.value.message,
        image_url: form.value.image_url,
        scheduled_at: form.value.scheduled_at || null,
        label_filter: JSON.stringify(form.value.selectedLabels.map(l => l.id)),
        inbox_filter: JSON.stringify(form.value.selectedInboxes.map(i => i.id)),
        channel_filter: JSON.stringify(
          form.value.selectedChannels.map(c => c.inbox_id)
        ),
        bonus_code_batch_id: form.value.bonusCodeBatchId || null,
        bonus_code_mode: form.value.bonusCodeBatchId
          ? form.value.bonusCodeMode
          : null,
      },
    });

    closeCreateForm();
    await loadBulkMessages();

    const message = form.value.scheduled_at
      ? t('BULK_MESSAGES.TOAST_MESSAGES.CREATE_SCHEDULED')
      : t('BULK_MESSAGES.TOAST_MESSAGES.CREATE_SUCCESS');

    useAlert(message);
  } catch (error) {
    useAlert(t('BULK_MESSAGES.TOAST_MESSAGES.CREATE_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const cancelBulkMessage = async id => {
  try {
    await authenticatedAxios.post(
      `${apiBase.value}/${id}/cancel`
    );
    await loadBulkMessages();
    useAlert(t('BULK_MESSAGES.TOAST_MESSAGES.CANCEL_SUCCESS'));
  } catch (error) {
    useAlert(t('BULK_MESSAGES.TOAST_MESSAGES.CANCEL_ERROR'));
  }
};

const openDeleteDialog = id => {
  selectedBulkMessageId.value = id;
  deleteDialogRef.value?.open();
};

const deleteBulkMessage = async () => {
  if (!selectedBulkMessageId.value) return;

  try {
    await authenticatedAxios.delete(`${apiBase.value}/${selectedBulkMessageId.value}`);
    await loadBulkMessages();
    useAlert(t('BULK_MESSAGES.TOAST_MESSAGES.DELETE_SUCCESS'));
    deleteDialogRef.value?.close();
  } catch (error) {
    useAlert(t('BULK_MESSAGES.TOAST_MESSAGES.DELETE_ERROR'));
  }
};

const pauseBulkMessage = async id => {
  try {
    await authenticatedAxios.post(
      `${apiBase.value}/${id}/pause`
    );
    await loadBulkMessages();
    useAlert(t('BULK_MESSAGES.TOAST_MESSAGES.PAUSE_SUCCESS'));
  } catch (error) {
    useAlert(t('BULK_MESSAGES.TOAST_MESSAGES.PAUSE_ERROR'));
  }
};

const resumeBulkMessage = async id => {
  try {
    await authenticatedAxios.post(
      `${apiBase.value}/${id}/resume`
    );
    await loadBulkMessages();
    useAlert(t('BULK_MESSAGES.TOAST_MESSAGES.RESUME_SUCCESS'));
  } catch (error) {
    useAlert(t('BULK_MESSAGES.TOAST_MESSAGES.RESUME_ERROR'));
  }
};

const openScheduleDialog = id => {
  selectedBulkMessageId.value = id;
  scheduledTime.value = '';
  scheduleDialogRef.value?.open();
};

const scheduleBulkMessage = async () => {
  if (!selectedBulkMessageId.value || !scheduledTime.value) return;

  try {
    await authenticatedAxios.post(
      `${apiBase.value}/${selectedBulkMessageId.value}/schedule`,
      {
        scheduled_at: scheduledTime.value,
      }
    );
    await loadBulkMessages();
    useAlert(t('BULK_MESSAGES.TOAST_MESSAGES.SCHEDULE_SUCCESS'));
    scheduleDialogRef.value?.close();
  } catch (error) {
    useAlert(t('BULK_MESSAGES.TOAST_MESSAGES.SCHEDULE_ERROR'));
  }
};

const canCancelBulkMessage = status => {
  return ['pending', 'scheduled', 'paused', 'resumed'].includes(status);
};

const updateProgressOnly = async () => {
  const activeMessages = bulkMessages.value.filter(bm =>
    ['processing', 'pending', 'resumed'].includes(bm.status)
  );
  if (activeMessages.length === 0) return;

  try {
    const response = await wootAPI.get();
    let updatedMessages = [];
    if (Array.isArray(response.data)) {
      updatedMessages = response.data;
    } else if (Array.isArray(response.data.payload)) {
      updatedMessages = response.data.payload;
    }

    bulkMessages.value = bulkMessages.value.map(bm => {
      if (['processing', 'pending', 'resumed'].includes(bm.status)) {
        const updated = updatedMessages.find(um => um.id === bm.id);
        return updated || bm;
      }
      return bm;
    });
  } catch (error) {
    // Error updating progress
  }
};

const startPolling = () => {
  pollingInterval.value = setInterval(() => {
    updateProgressOnly();
  }, 2000);
};

const stopPolling = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value);
    pollingInterval.value = null;
  }
};

const formatDate = dateString => {
  return new Date(dateString).toLocaleDateString();
};

const getStatusColor = status => {
  const colors = {
    pending: 'text-n-amber-11 bg-n-amber-2',
    processing: 'text-n-blue-11 bg-n-blue-2',
    completed: 'text-n-emerald-11 bg-n-emerald-2',
    failed: 'text-n-ruby-11 bg-n-ruby-2',
    cancelled: 'text-n-slate-11 bg-n-slate-2',
    scheduled: 'text-n-purple-11 bg-n-purple-2',
    paused: 'text-n-orange-11 bg-n-orange-2',
    resumed: 'text-n-teal-11 bg-n-teal-2',
  };
  return colors[status] || 'text-n-slate-11 bg-n-slate-2';
};

// Lifecycle
onMounted(async () => {
  await loadBulkMessages();
  await loadFilters();
  startPolling();
});

onUnmounted(() => {
  stopPolling();
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <!-- Header Section -->
    <div class="sticky top-0 z-20 bg-n-background border-b border-n-container">
      <div class="px-6 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 bg-n-blue-2 rounded-lg flex items-center justify-center"
            >
              <fluent-icon icon="mail" class="text-n-blue-11" size="20" />
            </div>
            <div>
              <h1 class="text-xl font-semibold text-n-slate-12">
                {{ t('BULK_MESSAGES.TITLE') }}
              </h1>
              <p class="text-sm text-n-slate-11">
                {{ t('BULK_MESSAGES.DESCRIPTION.SEND_TO_ALL') }}
              </p>
            </div>
          </div>
          <Button
            icon="i-lucide-circle-plus"
            :label="t('BULK_MESSAGES.NEW_BULK_MESSAGE')"
            @click="showCreateForm = true"
          />
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 overflow-auto p-6">
      <!-- Loading State -->
      <div v-if="isLoading" class="flex items-center justify-center py-20">
        <woot-loading-state :message="t('BULK_MESSAGES.LOADING')" />
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!bulkMessages.length"
        class="flex flex-col items-center justify-center py-20"
      >
        <div
          class="w-16 h-16 bg-n-slate-2 rounded-full flex items-center justify-center mb-4"
        >
          <fluent-icon icon="mail" class="text-n-slate-11" size="32" />
        </div>
        <h3 class="text-lg font-medium text-n-slate-12 mb-2">
          {{ t('BULK_MESSAGES.LIST.EMPTY_TITLE') }}
        </h3>
        <p class="text-n-slate-11 mb-6 text-center max-w-md">
          {{ t('BULK_MESSAGES.LIST.EMPTY_SUBTITLE') }}
        </p>
        <Button
          icon="i-lucide-circle-plus"
          :label="t('BULK_MESSAGES.LIST.EMPTY_BUTTON')"
          @click="showCreateForm = true"
        />
      </div>

      <!-- Content -->
      <div v-else>
        <!-- Stats Cards -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div
            class="flex items-center p-4 bg-n-solid-2 rounded-xl border border-n-container"
          >
            <div
              class="w-10 h-10 bg-n-blue-2 rounded-lg flex items-center justify-center mr-4 flex-shrink-0"
            >
              <fluent-icon icon="mail" class="text-n-blue-11" size="20" />
            </div>
            <div class="min-w-0">
              <p class="text-sm text-n-slate-11">
                {{ t('BULK_MESSAGES.STATS.TOTAL') }}
              </p>
              <p class="text-2xl font-semibold text-n-slate-12">
                {{ bulkMessages.length }}
              </p>
            </div>
          </div>

          <div
            class="flex items-center p-4 bg-n-solid-2 rounded-xl border border-n-container"
          >
            <div
              class="w-10 h-10 bg-n-emerald-2 rounded-lg flex items-center justify-center mr-4 flex-shrink-0"
            >
              <fluent-icon
                icon="checkmark-circle"
                class="text-n-emerald-11"
                size="20"
              />
            </div>
            <div class="min-w-0">
              <p class="text-sm text-n-slate-11">
                {{ t('BULK_MESSAGES.STATS.COMPLETED') }}
              </p>
              <p class="text-2xl font-semibold text-n-slate-12">
                {{
                  bulkMessages.filter(bm => bm.status === 'completed').length
                }}
              </p>
            </div>
          </div>

          <div
            class="flex items-center p-4 bg-n-solid-2 rounded-xl border border-n-container"
          >
            <div
              class="w-10 h-10 bg-n-blue-2 rounded-lg flex items-center justify-center mr-4 flex-shrink-0"
            >
              <fluent-icon icon="clock" class="text-n-blue-11" size="20" />
            </div>
            <div class="min-w-0">
              <p class="text-sm text-n-slate-11">
                {{ t('BULK_MESSAGES.STATS.PROCESSING') }}
              </p>
              <p class="text-2xl font-semibold text-n-slate-12">
                {{
                  bulkMessages.filter(bm => bm.status === 'processing').length
                }}
              </p>
            </div>
          </div>

          <div
            class="flex items-center p-4 bg-n-solid-2 rounded-xl border border-n-container"
          >
            <div
              class="w-10 h-10 bg-n-purple-2 rounded-lg flex items-center justify-center mr-4 flex-shrink-0"
            >
              <fluent-icon icon="calendar" class="text-n-purple-11" size="20" />
            </div>
            <div class="min-w-0">
              <p class="text-sm text-n-slate-11">
                {{ t('BULK_MESSAGES.STATS.SCHEDULED') }}
              </p>
              <p class="text-2xl font-semibold text-n-slate-12">
                {{
                  bulkMessages.filter(bm => bm.status === 'scheduled').length
                }}
              </p>
            </div>
          </div>
        </div>
        <!-- FIXED: closed the stats cards div above -->

        <!-- Bulk Messages Table -->
        <div
          class="bg-n-solid-2 rounded-xl border border-n-container overflow-hidden"
        >
          <div class="px-6 py-4 border-b border-n-container">
            <h3 class="text-lg font-medium text-n-slate-12">
              {{ t('BULK_MESSAGES.LIST.TITLE') }}
            </h3>
            <p class="text-sm text-n-slate-11 mt-1">
              {{ t('BULK_MESSAGES.LIST.SUBTITLE') }}
            </p>
          </div>

          <div v-if="bulkMessages.length === 0" class="p-12 text-center">
            <div
              class="w-16 h-16 bg-n-slate-2 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <fluent-icon icon="mail" class="text-n-slate-11" size="32" />
            </div>
            <h3 class="text-lg font-medium text-n-slate-12 mb-2">
              {{ t('BULK_MESSAGES.LIST.EMPTY_TITLE') }}
            </h3>
            <p class="text-n-slate-11 mb-6">
              {{ t('BULK_MESSAGES.LIST.EMPTY_SUBTITLE') }}
            </p>
            <Button
              icon="i-lucide-circle-plus"
              :label="t('BULK_MESSAGES.LIST.EMPTY_BUTTON')"
              @click="showCreateForm = true"
            />
          </div>

          <div v-else class="divide-y divide-n-container">
            <div
              v-for="bulkMessage in bulkMessages"
              :key="bulkMessage.id"
              class="p-4 sm:p-6 hover:bg-n-slate-1 transition-colors duration-200"
            >
              <div
                class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4"
              >
                <div class="flex-1 min-w-0">
                  <div
                    class="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2"
                  >
                    <h4 class="text-lg font-medium text-n-slate-12 truncate">
                      {{ bulkMessage.name }}
                    </h4>
                    <span
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium w-fit"
                      :class="getStatusColor(bulkMessage.status)"
                    >
                      {{ statusMap[bulkMessage.status] }}
                    </span>
                  </div>

                  <p class="text-sm text-n-slate-11 mb-4 line-clamp-2">
                    {{ bulkMessage.message }}
                  </p>

                  <!-- Stats -->
                  <div
                    class="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center gap-3 sm:gap-6 text-sm text-n-slate-11 mb-4"
                  >
                    <div class="flex items-center">
                      <fluent-icon
                        icon="people"
                        class="w-4 h-4 mr-1 flex-shrink-0"
                      />
                      <span class="font-medium">{{
                        bulkMessage.total_count
                      }}</span>
                      <span class="hidden sm:inline">{{
                        t('BULK_MESSAGES.LIST.TARGET')
                      }}</span>
                    </div>
                    <div class="flex items-center">
                      <fluent-icon
                        icon="checkmark-circle"
                        class="w-4 h-4 mr-1 text-n-emerald-11 flex-shrink-0"
                      />
                      <span class="font-medium">{{
                        bulkMessage.sent_count
                      }}</span>
                      <span class="hidden sm:inline">{{
                        t('BULK_MESSAGES.LIST.SENT')
                      }}</span>
                    </div>
                    <div
                      v-if="bulkMessage.failed_count > 0"
                      class="flex items-center"
                    >
                      <fluent-icon
                        icon="error-circle"
                        class="w-4 h-4 mr-1 text-n-ruby-11 flex-shrink-0"
                      />
                      <span class="font-medium">{{
                        bulkMessage.failed_count
                      }}</span>
                      <span class="hidden sm:inline">{{
                        t('BULK_MESSAGES.LIST.ERROR')
                      }}</span>
                    </div>
                    <div class="flex items-center col-span-2 sm:col-span-1">
                      <fluent-icon
                        icon="clock"
                        class="w-4 h-4 mr-1 flex-shrink-0"
                      />
                      {{ formatDate(bulkMessage.created_at) }}
                    </div>
                  </div>

                  <!-- Progress Bar -->
                  <div v-if="bulkMessage.status === 'processing'" class="mb-4">
                    <div
                      class="flex items-center justify-between text-sm text-n-slate-11 mb-2"
                    >
                      <span>{{ t('BULK_MESSAGES.LIST.PROGRESS') }}</span>
                      <span class="font-medium">
                        {{ bulkMessage.progress_percentage }}%
                      </span>
                    </div>
                    <div class="w-full bg-n-slate-3 rounded-full h-2">
                      <div
                        class="bg-n-blue-9 h-2 rounded-full transition-all duration-500 ease-out"
                        :style="{
                          width: `${bulkMessage.progress_percentage}%`,
                        }"
                      />
                    </div>
                  </div>
                </div>

                <!-- Actions -->
                <div class="flex flex-wrap items-center gap-2 sm:ml-4">
                  <Button
                    icon="i-lucide-bar-chart-3"
                    :label="t('BULK_MESSAGES.ACTIONS.STATS')"
                    slate
                    xs
                    @click="openStatsModal(bulkMessage.id)"
                  />
                  <Button
                    v-if="bulkMessage.status === 'processing'"
                    icon="i-lucide-pause"
                    :label="t('BULK_MESSAGES.ACTIONS.PAUSE')"
                    slate
                    xs
                    @click="pauseBulkMessage(bulkMessage.id)"
                  />

                  <Button
                    v-if="bulkMessage.status === 'paused'"
                    icon="i-lucide-play"
                    :label="t('BULK_MESSAGES.ACTIONS.RESUME')"
                    blue
                    xs
                    @click="resumeBulkMessage(bulkMessage.id)"
                  />

                  <Button
                    v-if="bulkMessage.status === 'scheduled'"
                    icon="i-lucide-calendar"
                    :label="t('BULK_MESSAGES.ACTIONS.SCHEDULE')"
                    purple
                    xs
                    @click="openScheduleDialog(bulkMessage.id)"
                  />

                  <Button
                    v-if="canCancelBulkMessage(bulkMessage.status)"
                    icon="i-lucide-x"
                    :label="t('BULK_MESSAGES.ACTIONS.CANCEL')"
                    slate
                    xs
                    @click="cancelBulkMessage(bulkMessage.id)"
                  />

                  <Button
                    icon="i-lucide-trash-2"
                    :label="t('BULK_MESSAGES.ACTIONS.DELETE')"
                    ruby
                    xs
                    @click="openDeleteDialog(bulkMessage.id)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Create Form Modal -->
  <woot-modal
    v-model:show="showCreateForm"
    size="large"
    :on-close="closeCreateForm"
  >
    <div class="p-6">
      <div class="flex items-center justify-between mb-6">
        <div>
          <h2 class="text-xl font-semibold text-n-slate-12">
            {{ t('BULK_MESSAGES.MODAL.TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11 mt-1">
            {{ t('BULK_MESSAGES.MODAL.SUBTITLE') }}
          </p>
        </div>
        <button
          class="text-n-slate-11 hover:text-n-slate-12 transition-colors duration-200"
          @click="closeCreateForm"
        >
          <fluent-icon icon="dismiss" size="20" />
        </button>
      </div>

      <form @submit.prevent="createBulkMessage">
        <!-- Basic Info -->
        <div class="grid grid-cols-1 gap-6 mb-6">
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              {{ t('BULK_MESSAGES.FORM.MESSAGE_NAME') }} *
            </label>
            <input
              v-model="form.name"
              type="text"
              required
              class="w-full px-4 py-3 border border-n-container rounded-lg focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-transparent bg-n-solid-2 text-n-slate-12 transition-colors duration-200"
              :placeholder="t('BULK_MESSAGES.FORM.MESSAGE_NAME_PLACEHOLDER')"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              {{ t('BULK_MESSAGES.FORM.MESSAGE_CONTENT') }} *
            </label>
            <textarea
              v-model="form.message"
              required
              rows="4"
              class="w-full px-4 py-3 border border-n-container rounded-lg focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-transparent bg-n-solid-2 text-n-slate-12 transition-colors duration-200 resize-none"
              :placeholder="t('BULK_MESSAGES.FORM.MESSAGE_CONTENT_PLACEHOLDER')"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              {{ t('BULK_MESSAGES.FORM.IMAGE_URL') }}
            </label>
            <input
              v-model="form.image_url"
              type="url"
              class="w-full px-4 py-3 border border-n-container rounded-lg focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-transparent bg-n-solid-2 text-n-slate-12 transition-colors duration-200"
              :placeholder="t('BULK_MESSAGES.FORM.IMAGE_URL_PLACEHOLDER')"
            />

            <div class="flex items-center gap-3 mt-2">
              <input
                ref="imageFileInput"
                type="file"
                accept="image/*"
                class="hidden"
                @change="onImageFileChange"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                :disabled="isUploadingImage"
                :label="isUploadingImage ? t('BULK_MESSAGES.FORM.IMAGE_UPLOADING') : t('BULK_MESSAGES.FORM.IMAGE_UPLOAD_BUTTON')"
                @click="imageFileInput?.click()"
              />
              <Button
                v-if="form.image_url"
                type="button"
                variant="outline"
                size="sm"
                color="ruby"
                :label="t('BULK_MESSAGES.FORM.IMAGE_CLEAR')"
                @click="clearImage"
              />
            </div>

            <div v-if="form.image_url" class="mt-3">
              <p class="text-xs text-n-slate-11 mb-2">
                {{ t('BULK_MESSAGES.FORM.IMAGE_PREVIEW') }}
              </p>
              <img
                :src="form.image_url"
                alt="preview"
                class="max-h-48 rounded-lg border border-n-container object-contain bg-n-solid-2"
              />
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              {{ t('BULK_MESSAGES.FORM.SCHEDULED_SENDING') }}
            </label>
            <div
              class="flex flex-col sm:flex-row items-stretch sm:items-center gap-3"
            >
              <input
                v-model="form.scheduled_at"
                type="datetime-local"
                class="flex-1 px-4 py-3 border border-n-container rounded-lg focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-transparent bg-n-solid-2 text-n-slate-12 transition-colors duration-200"
              />
              <Button
                type="button"
                slate
                :label="t('BULK_MESSAGES.FORM.CLEAR')"
                @click="form.scheduled_at = ''"
              />
            </div>
            <p class="text-xs text-n-slate-11 mt-1">
              {{ t('BULK_MESSAGES.FORM.SCHEDULED_HELP') }}
            </p>
          </div>
        </div>

        <!-- Filters -->
        <div class="border-t border-n-container pt-6">
          <h3 class="text-lg font-medium text-n-slate-12 mb-4">
            {{ t('BULK_MESSAGES.FORM.TARGET_FILTERS') }}
          </h3>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">
                {{ t('BULK_MESSAGES.FORM.LABEL_FILTER') }}
              </label>
              <Multiselect
                v-model="form.selectedLabels"
                :options="labelOptions"
                multiple
                :placeholder="t('BULK_MESSAGES.FORM.LABEL_FILTER_PLACEHOLDER')"
                label="title"
                track-by="id"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">
                {{ t('BULK_MESSAGES.FORM.INBOX_FILTER') }}
              </label>
              <Multiselect
                v-model="form.selectedInboxes"
                :options="inboxOptions"
                multiple
                :placeholder="t('BULK_MESSAGES.FORM.INBOX_FILTER_PLACEHOLDER')"
                label="name"
                track-by="id"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">
                {{ t('BULK_MESSAGES.FORM.CHANNEL_FILTER') }}
              </label>
              <Multiselect
                v-model="form.selectedChannels"
                :options="channelOptions"
                multiple
                :placeholder="
                  t('BULK_MESSAGES.FORM.CHANNEL_FILTER_PLACEHOLDER')
                "
                label="label"
                track-by="value"
              />
            </div>
          </div>
        </div>

        <!-- Bonus Code -->
        <div class="border-t border-n-container pt-6 mt-6">
          <h3 class="text-lg font-medium text-n-slate-12 mb-1">
            {{ t('BULK_MESSAGES.FORM.BONUS_TITLE') }}
          </h3>
          <p class="text-xs text-n-slate-11 mb-4">
            {{ t('BULK_MESSAGES.FORM.BONUS_HELP') }}
          </p>
          <div
            v-if="!availableBatches.length"
            class="p-4 bg-n-amber-2 rounded-lg text-sm text-n-amber-11"
          >
            {{ t('BULK_MESSAGES.FORM.BONUS_NO_BATCH') }}
          </div>
          <div v-else class="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">
                {{ t('BULK_MESSAGES.FORM.BONUS_BATCH') }}
              </label>
              <select
                v-model="form.bonusCodeBatchId"
                class="w-full px-4 py-3 border border-n-container rounded-lg bg-n-solid-2 text-n-slate-12"
              >
                <option value="">
                  {{ t('BULK_MESSAGES.FORM.BONUS_BATCH_NONE') }}
                </option>
                <option v-for="b in availableBatches" :key="b.id" :value="b.id">
                  {{ b.name }} ({{ b.available_count }}
                  {{ t('BULK_MESSAGES.FORM.BONUS_AVAILABLE') }})
                </option>
              </select>
            </div>
            <div v-if="form.bonusCodeBatchId">
              <label class="block text-sm font-medium text-n-slate-12 mb-2">
                {{ t('BULK_MESSAGES.FORM.BONUS_MODE') }}
              </label>
              <div class="flex flex-col gap-2 mt-1">
                <label class="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    v-model="form.bonusCodeMode"
                    type="radio"
                    value="pool"
                  />
                  <span>{{ t('BULK_MESSAGES.FORM.BONUS_MODE_POOL') }}</span>
                </label>
                <label class="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    v-model="form.bonusCodeMode"
                    type="radio"
                    value="mapping"
                  />
                  <span>{{ t('BULK_MESSAGES.FORM.BONUS_MODE_MAPPING') }}</span>
                </label>
              </div>
            </div>
          </div>
          <p
            v-if="form.bonusCodeBatchId"
            class="text-xs text-n-amber-11 mt-3"
          >
            {{ t('BULK_MESSAGES.FORM.BONUS_PLACEHOLDER_WARNING') }}
          </p>
        </div>

        <!-- Preview -->
        <div
          v-if="previewCount !== null"
          class="mt-6 p-4 bg-n-blue-2 rounded-lg"
        >
          <p class="text-sm text-n-blue-11">
            {{
              t('BULK_MESSAGES.PREVIEW.TARGET_COUNT', { count: previewCount })
            }}
          </p>
        </div>

        <!-- Actions -->
        <div
          class="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 mt-8 pt-6 border-t border-n-container"
        >
          <Button
            type="button"
            slate
            :label="t('BULK_MESSAGES.MODAL.CANCEL')"
            @click="closeCreateForm"
          />
          <Button
            type="button"
            slate
            :label="t('BULK_MESSAGES.MODAL.PREVIEW')"
            @click="previewBulkMessage"
          />
          <Button
            type="button"
            slate
            :label="t('BULK_MESSAGES.MODAL.DETAIL')"
            @click="openDetailModal"
          />
          <Button
            type="submit"
            blue
            :label="t('BULK_MESSAGES.MODAL.CREATE')"
            :is-loading="isLoading"
            :disabled="!form.name || !form.message"
          />
        </div>
      </form>
    </div>
  </woot-modal>

  <!-- Delete Confirmation Dialog -->
  <Dialog
    ref="deleteDialogRef"
    type="alert"
    :title="t('BULK_MESSAGES.CONFIRM.DELETE_TITLE')"
    :description="t('BULK_MESSAGES.CONFIRM.DELETE_DESCRIPTION')"
    :confirm-button-label="t('BULK_MESSAGES.CONFIRM.DELETE_CONFIRM')"
    :cancel-button-label="t('BULK_MESSAGES.CONFIRM.DELETE_CANCEL')"
    @confirm="deleteBulkMessage"
  />

  <!-- Schedule Dialog -->
  <Dialog
    ref="scheduleDialogRef"
    type="edit"
    :title="t('BULK_MESSAGES.CONFIRM.SCHEDULE_TITLE')"
    :description="t('BULK_MESSAGES.CONFIRM.SCHEDULE_DESCRIPTION')"
    :confirm-button-label="t('BULK_MESSAGES.CONFIRM.SCHEDULE_CONFIRM')"
    :cancel-button-label="t('BULK_MESSAGES.CONFIRM.SCHEDULE_CANCEL')"
    @confirm="scheduleBulkMessage"
  >
    <div class="mt-4">
      <label class="block text-sm font-medium text-n-slate-12 mb-2">
        {{ t('BULK_MESSAGES.CONFIRM.SCHEDULE_DATETIME_LABEL') }}
      </label>
      <input
        v-model="scheduledTime"
        type="datetime-local"
        required
        class="w-full px-4 py-3 border border-n-container rounded-lg focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-transparent bg-n-solid-2 text-n-slate-12 transition-colors duration-200"
        :placeholder="t('BULK_MESSAGES.CONFIRM.SCHEDULE_DATETIME_PLACEHOLDER')"
      />
      <p class="text-xs text-n-slate-11 mt-1">
        {{ t('BULK_MESSAGES.CONFIRM.SCHEDULE_HELP') }}
      </p>
    </div>
  </Dialog>

  <!-- Target Conversations Detail Modal -->
  <woot-modal
    v-model:show="showDetailModal"
    size="large"
    :on-close="closeDetailModal"
  >
    <div class="p-6">
      <div class="flex items-center justify-between mb-6">
        <div>
          <h2 class="text-xl font-semibold text-n-slate-12">
            {{ t('BULK_MESSAGES.DETAIL.TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11 mt-1">
            {{
              t('BULK_MESSAGES.DETAIL.SUBTITLE', { count: detailTotal })
            }}
          </p>
        </div>
        <button
          class="text-n-slate-11 hover:text-n-slate-12 transition-colors duration-200"
          @click="closeDetailModal"
        >
          <fluent-icon icon="dismiss" size="20" />
        </button>
      </div>

      <div v-if="detailLoading" class="flex items-center justify-center py-10">
        <woot-loading-state :message="t('BULK_MESSAGES.DETAIL.LOADING')" />
      </div>

      <div v-else-if="!detailConversations.length" class="py-10 text-center">
        <fluent-icon
          icon="info"
          class="text-n-slate-11 mx-auto mb-3"
          size="28"
        />
        <p class="text-n-slate-11">{{ t('BULK_MESSAGES.DETAIL.EMPTY') }}</p>
      </div>

      <div v-else>
        <div class="overflow-x-auto border border-n-container rounded-lg">
          <table class="min-w-full text-sm">
            <thead class="bg-n-slate-2">
              <tr>
                <th class="px-4 py-2 text-left font-medium text-n-slate-12">
                  #
                </th>
                <th class="px-4 py-2 text-left font-medium text-n-slate-12">
                  {{ t('BULK_MESSAGES.DETAIL.CONTACT') }}
                </th>
                <th class="px-4 py-2 text-left font-medium text-n-slate-12">
                  {{ t('BULK_MESSAGES.DETAIL.INBOX') }}
                </th>
                <th class="px-4 py-2 text-left font-medium text-n-slate-12">
                  {{ t('BULK_MESSAGES.DETAIL.STATUS') }}
                </th>
                <th class="px-4 py-2 text-left font-medium text-n-slate-12">
                  {{ t('BULK_MESSAGES.DETAIL.CREATED_AT') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-container">
              <tr
                v-for="conv in detailConversations"
                :key="conv.conversation_id"
                class="hover:bg-n-slate-1"
              >
                <td class="px-4 py-2 text-n-slate-12">#{{ conv.id }}</td>
                <td class="px-4 py-2 text-n-slate-12">
                  {{ conv.contact_name || '—' }}
                </td>
                <td class="px-4 py-2 text-n-slate-11">
                  {{ conv.inbox_name }}
                </td>
                <td class="px-4 py-2 text-n-slate-11">{{ conv.status }}</td>
                <td class="px-4 py-2 text-n-slate-11">
                  {{ formatDate(conv.created_at) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          v-if="detailTotalPages > 1"
          class="flex items-center justify-between mt-4"
        >
          <p class="text-sm text-n-slate-11">
            {{
              t('BULK_MESSAGES.DETAIL.PAGE_INFO', {
                page: detailPage,
                total: detailTotalPages,
              })
            }}
          </p>
          <div class="flex items-center gap-2">
            <Button
              slate
              xs
              :disabled="detailPage <= 1 || detailLoading"
              :label="t('BULK_MESSAGES.DETAIL.PREV')"
              @click="goToDetailPage(detailPage - 1)"
            />
            <Button
              slate
              xs
              :disabled="detailPage >= detailTotalPages || detailLoading"
              :label="t('BULK_MESSAGES.DETAIL.NEXT')"
              @click="goToDetailPage(detailPage + 1)"
            />
          </div>
        </div>
      </div>

      <div class="flex justify-end mt-6 pt-6 border-t border-n-container">
        <Button
          type="button"
          slate
          :label="t('BULK_MESSAGES.DETAIL.CLOSE')"
          @click="closeDetailModal"
        />
      </div>
    </div>
  </woot-modal>

  <!-- Stats Modal -->
  <woot-modal
    v-model:show="showStatsModal"
    size="xlarge"
    :on-close="closeStatsModal"
  >
    <div class="p-6">
      <div class="flex items-center justify-between mb-6">
        <div>
          <h2 class="text-xl font-semibold text-n-slate-12">
            {{ t('BULK_MESSAGES.STATS_MODAL.TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11 mt-1">
            {{ t('BULK_MESSAGES.STATS_MODAL.SUBTITLE') }}
          </p>
        </div>
        <button
          class="text-n-slate-11 hover:text-n-slate-12"
          @click="closeStatsModal"
        >
          <fluent-icon icon="dismiss" size="20" />
        </button>
      </div>

      <div v-if="statsLoading" class="flex items-center justify-center py-10">
        <woot-loading-state :message="t('BULK_MESSAGES.STATS_MODAL.LOADING')" />
      </div>

      <div v-else-if="statsData" class="flex flex-col gap-6">
        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <div class="p-4 rounded-xl border border-n-container bg-n-solid-2">
            <p class="text-xs text-n-slate-11">
              {{ t('BULK_MESSAGES.STATS_MODAL.TARGET') }}
            </p>
            <p class="text-xl font-semibold text-n-slate-12">
              {{ statsData.target_count }}
            </p>
          </div>
          <div class="p-4 rounded-xl border border-n-container bg-n-emerald-2">
            <p class="text-xs text-n-emerald-11">
              {{ t('BULK_MESSAGES.STATS_MODAL.SENT') }}
            </p>
            <p class="text-xl font-semibold text-n-emerald-11">
              {{ statsData.status_breakdown.sent }}
            </p>
          </div>
          <div class="p-4 rounded-xl border border-n-container bg-n-ruby-2">
            <p class="text-xs text-n-ruby-11">
              {{ t('BULK_MESSAGES.STATS_MODAL.FAILED') }}
            </p>
            <p class="text-xl font-semibold text-n-ruby-11">
              {{ statsData.status_breakdown.failed }}
            </p>
          </div>
          <div class="p-4 rounded-xl border border-n-container bg-n-amber-2">
            <p class="text-xs text-n-amber-11">
              {{ t('BULK_MESSAGES.STATS_MODAL.NOT_ATTEMPTED') }}
            </p>
            <p class="text-xl font-semibold text-n-amber-11">
              {{ statsData.not_attempted_count }}
            </p>
          </div>
          <div class="p-4 rounded-xl border border-n-container bg-n-solid-2">
            <p class="text-xs text-n-slate-11">
              {{ t('BULK_MESSAGES.STATS_MODAL.SUCCESS_RATE') }}
            </p>
            <p class="text-xl font-semibold text-n-slate-12">
              {{ statsData.success_rate }}%
            </p>
          </div>
        </div>

        <div>
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
            <h3 class="text-base font-medium text-n-slate-12">
              {{
                t('BULK_MESSAGES.STATS_MODAL.FAILED_LIST', {
                  count: failedTotal,
                })
              }}
            </h3>
            <div class="flex flex-wrap items-center gap-3">
              <label class="flex items-center gap-2 text-sm">
                <span class="text-n-slate-11">
                  {{ t('BULK_MESSAGES.STATS_MODAL.FILTER_LABEL') }}
                </span>
                <select
                  v-model="failedErrorKind"
                  class="px-2 py-1 border border-n-container rounded-md text-sm bg-n-solid-2 text-n-slate-12"
                  @change="applyFailedFilters"
                >
                  <option
                    v-for="opt in ERROR_KIND_OPTIONS"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ t(opt.labelKey) }}<template v-if="opt.value && failedErrorKindCounts[opt.value] !== undefined">
                      ({{ failedErrorKindCounts[opt.value] }})</template>
                  </option>
                </select>
              </label>
              <label class="flex items-center gap-2 text-sm">
                <span class="text-n-slate-11">
                  {{ t('BULK_MESSAGES.STATS_MODAL.PER_PAGE_LABEL') }}
                </span>
                <select
                  v-model.number="failedPerPage"
                  class="px-2 py-1 border border-n-container rounded-md text-sm bg-n-solid-2 text-n-slate-12"
                  @change="applyFailedFilters"
                >
                  <option v-for="n in PER_PAGE_OPTIONS" :key="n" :value="n">
                    {{ n }}
                  </option>
                </select>
              </label>
            </div>
          </div>
          <div
            v-if="failedLoading"
            class="flex items-center justify-center py-6"
          >
            <woot-loading-state />
          </div>
          <div
            v-else-if="!failedMessages.length"
            class="py-6 text-center text-n-slate-11 border border-n-container rounded-lg"
          >
            {{ t('BULK_MESSAGES.STATS_MODAL.NO_FAILED') }}
          </div>
          <div v-else class="overflow-x-auto border border-n-container rounded-lg">
            <table class="min-w-full text-sm">
              <thead class="bg-n-slate-2">
                <tr>
                  <th class="px-4 py-2 text-left">
                    {{ t('BULK_MESSAGES.STATS_MODAL.COL_CONVERSATION') }}
                  </th>
                  <th class="px-4 py-2 text-left">
                    {{ t('BULK_MESSAGES.STATS_MODAL.COL_CONTACT') }}
                  </th>
                  <th class="px-4 py-2 text-left">
                    {{ t('BULK_MESSAGES.STATS_MODAL.COL_ERROR') }}
                  </th>
                  <th class="px-4 py-2 text-left">
                    {{ t('BULK_MESSAGES.STATS_MODAL.COL_AT') }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-container">
                <tr
                  v-for="m in failedMessages"
                  :key="m.message_id"
                  class="hover:bg-n-slate-2 cursor-pointer transition-colors"
                  :title="t('BULK_MESSAGES.STATS_MODAL.OPEN_CONVERSATION_HINT')"
                  @click="openConversation(m.conversation_display_id)"
                >
                  <td class="px-4 py-2 text-n-blue-11 font-medium">
                    #{{ m.conversation_display_id || m.conversation_id }}
                  </td>
                  <td class="px-4 py-2 text-n-slate-12">
                    {{ m.contact_name || '—' }}
                  </td>
                  <td class="px-4 py-2 text-n-ruby-11">
                    {{ friendlyError(m.error) }}
                  </td>
                  <td class="px-4 py-2 text-n-slate-11">
                    {{ formatDate(m.created_at) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div
            v-if="failedTotalPages > 1"
            class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-3 flex-wrap"
          >
            <div class="flex items-center gap-2 text-sm text-n-slate-11 flex-nowrap whitespace-nowrap">
              <span class="flex-shrink-0">
                {{ t('BULK_MESSAGES.STATS_MODAL.PAGE_GOTO') }}
              </span>
              <input
                v-model.number="failedPageInput"
                type="number"
                :min="1"
                :max="failedTotalPages"
                class="w-20 px-2 py-1 border border-n-container rounded-md text-sm bg-n-solid-2 text-n-slate-12 text-center flex-shrink-0"
                @keyup.enter="submitPageJump"
              />
              <span class="flex-shrink-0">/ {{ failedTotalPages }}</span>
              <Button
                slate
                xs
                class="flex-shrink-0"
                :disabled="failedLoading"
                :label="t('BULK_MESSAGES.STATS_MODAL.GO')"
                @click="submitPageJump"
              />
            </div>
            <div class="flex gap-2 flex-shrink-0">
              <Button
                slate
                xs
                :disabled="failedPage <= 1 || failedLoading"
                :label="t('BULK_MESSAGES.STATS_MODAL.PREV')"
                @click="goToFailedPage(failedPage - 1)"
              />
              <Button
                slate
                xs
                :disabled="failedPage >= failedTotalPages || failedLoading"
                :label="t('BULK_MESSAGES.STATS_MODAL.NEXT')"
                @click="goToFailedPage(failedPage + 1)"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-end mt-6 pt-6 border-t border-n-container">
        <Button
          type="button"
          slate
          :label="t('BULK_MESSAGES.STATS_MODAL.CLOSE')"
          @click="closeStatsModal"
        />
      </div>
    </div>
  </woot-modal>
</template>
